import { v4 as uuidv4 } from 'uuid';

import { debugLog, finleyAssert } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';
import type {
  TapeBuilderEdge,
  TapeBuilderNodeAbstract,
} from '@finley-technologies/tape-builder';

import { GraphPositions } from 'packages/common-frontend/components/ReactFlow';

export type NormalizedGraph = {
  nodes: TapeBuilderNodeAbstract[];
  edges: TapeBuilderEdge[];
  positions: GraphPositions;
};

// normalizes the graph by assigning stable ids to nodes and edges.
// this ensures that the graph can be compared and merged easily.
export const normalizeGraph = ({
  nodes,
  edges,
  positions,
}: {
  nodes: TapeBuilderNodeAbstract[];
  edges: TapeBuilderEdge[];
  positions: GraphPositions;
}): {
  normalized: NormalizedGraph;
  nodeIdToSlug: Map<string, string>; // nodeId => slug
} => {
  const nodeIdToSlug = new Map<string, string>();
  const nodeSlugToId = new Map<string, string>();

  // in 1st pass collect all the artifactSlugs and ensure they are unique
  // also create a map of nodeId => slug for all tape nodes.

  nodes.forEach((node) => {
    if (
      TapeBuilderUI.isTapeBuilderFileNode(node) ||
      TapeBuilderUI.tapeNodes.db_node.isNode(node)
    ) {
      // tape nodes have artifactSlugs
      const nodeSlug = node.configuration.artifactSlug;
      finleyAssert(
        !nodeIdToSlug.has(node.metadata.tapeBuilderNodeId),
        `duplicate [tapeBuilderNodeId:${node.metadata.tapeBuilderNodeId}] found in graph`,
      );
      finleyAssert(
        !nodeSlugToId.has(nodeSlug),
        `duplicate [artifactSlug:${nodeSlug}] found in graph`,
      );
      nodeSlugToId.set(nodeSlug, node.metadata.tapeBuilderNodeId);
      nodeIdToSlug.set(node.metadata.tapeBuilderNodeId, nodeSlug);
    }
  });

  // in 2nd pass we will look at operation nodes,
  // and assign them slug based on the nodes they are connected to.
  nodes.forEach((node) => {
    if (
      TapeBuilderUI.isTapeBuilderFileNode(node) ||
      TapeBuilderUI.tapeNodes.db_node.isNode(node)
    ) {
      return;
    }
    const { metadata } = node;

    // since this is operation node, we make up slug
    // by combining source and target slugs + operation
    // like: `${sourceSlugs}=>${operation}=>${targetSlugs}`
    const sourceSlugs = edges
      .filter((edge) => edge.target === metadata.tapeBuilderNodeId)
      .map((edge) => nodeIdToSlug.get(edge.source))
      .sort()
      .join('+');

    const targetSlugs = edges
      .filter((edge) => edge.source === metadata.tapeBuilderNodeId)
      .map((edge) => nodeIdToSlug.get(edge.target))
      .sort()
      .join('+');

    const operation = node.metadata.tapeBuilderNodeType;

    const nodeSlug = `${sourceSlugs}=>${operation}=>${targetSlugs}`;
    finleyAssert(
      !nodeSlugToId.has(nodeSlug),
      `duplicate [nodeSlug:${nodeSlug}] found in graph. Do you have duplicate edges?`,
    );
    finleyAssert(
      !nodeIdToSlug.has(metadata.tapeBuilderNodeId),
      `duplicate [tapeBuilderNodeId:${metadata.tapeBuilderNodeId}] found in graph`,
    );
    nodeIdToSlug.set(metadata.tapeBuilderNodeId, nodeSlug);
    nodeSlugToId.set(nodeSlug, metadata.tapeBuilderNodeId);
  });

  const normalizedNodes = nodes.map((node) => {
    const nodeSlug = nodeIdToSlug.get(node.metadata.tapeBuilderNodeId);
    finleyAssert(nodeSlug, 'node slug not found');
    return {
      ...node,
      metadata: { ...node.metadata, tapeBuilderNodeId: nodeSlug },
    };
  });

  const normalizedEdges = edges.map(({ source, target }) => {
    const sourceSlug = nodeIdToSlug.get(source);
    const targetSlug = nodeIdToSlug.get(target);
    finleyAssert(sourceSlug, `Edge: [source:${source}] slug not found`);
    finleyAssert(targetSlug, `Edge: [target:${target}] slug not found`);

    return { source: sourceSlug, target: targetSlug };
  });

  const normalizedPositions = Object.entries(positions).reduce<GraphPositions>(
    (acc, [id, position]) => {
      const slug = nodeIdToSlug.get(id);
      finleyAssert(slug, `position: [id:${id}] slug not found`);
      // eslint-disable-next-line no-param-reassign
      acc[slug] = position;
      return acc;
    },
    {},
  );

  return {
    normalized: {
      nodes: normalizedNodes,
      edges: normalizedEdges,
      positions: normalizedPositions,
    },
    nodeIdToSlug,
  };
};

export const deNormalizeGraph = (params: {
  normalized: NormalizedGraph;
  nodeIdToSlug: Map<string, string> | null;
}): {
  nodes: TapeBuilderNodeAbstract[];
  edges: TapeBuilderEdge[];
  positions: GraphPositions;
} => {
  const {
    nodes: normalizedNodes,
    edges: normalizedEdges,
    positions: normalizedPositions,
  } = params.normalized;

  // generate a map of slug => nodeId
  // from given nodeId => slug map
  const slugToNodeId = new Map<string, string>();
  if (params.nodeIdToSlug) {
    params.nodeIdToSlug.forEach((slug, nodeId) => {
      slugToNodeId.set(slug, nodeId);
    });
  }

  const nodes = normalizedNodes.map((node) => {
    const { metadata } = node;
    // use the stable Id if it was given
    const newId = slugToNodeId.get(metadata.tapeBuilderNodeId) || uuidv4();
    slugToNodeId.set(metadata.tapeBuilderNodeId, newId);

    return {
      ...node,
      metadata: { ...metadata, tapeBuilderNodeId: newId },
    };
  });

  const edges = normalizedEdges.map(({ source, target }) => {
    const sourceId = slugToNodeId.get(source);
    const targetId = slugToNodeId.get(target);
    finleyAssert(sourceId, `Edge: [source:${source}] id not found`);
    finleyAssert(targetId, `Edge: [target:${target}] id not found`);

    return { source: sourceId, target: targetId };
  });

  const positions = Object.entries(normalizedPositions).reduce<GraphPositions>(
    (acc, [slug, position]) => {
      const id = slugToNodeId.get(slug);
      if (!id) {
        debugLog('deNormalizeGraph', `position: [slug:${slug}] id not found`);
      } else {
        // eslint-disable-next-line no-param-reassign
        acc[id] = position;
      }
      return acc;
    },
    {},
  );

  return { nodes, edges, positions };
};

// merges given array of normalized graphs.
// picks nodes from left to right. if there are duplicate nodes, it will keep the first one.
export const mergeGraphs = (graphs: NormalizedGraph[]): NormalizedGraph => {
  const allNodes: TapeBuilderNodeAbstract[] = [];
  const allEdges: TapeBuilderEdge[] = [];
  const allPositions: GraphPositions = {};

  const nodeIds = new Set<string>();
  const edgeIds = new Set<string>();
  graphs.forEach((graph) => {
    graph.nodes.forEach((node) => {
      if (!nodeIds.has(node.metadata.tapeBuilderNodeId)) {
        allNodes.push(node);
        nodeIds.add(node.metadata.tapeBuilderNodeId);
      }
    });

    graph.edges.forEach((edge) => {
      const edgeId = `${edge.source}#${edge.target}`;
      if (!edgeIds.has(edgeId)) {
        allEdges.push(edge);
        edgeIds.add(edgeId);
      }
    });

    Object.entries(graph.positions).forEach(([id, position]) => {
      allPositions[id] = position;
    });
  });

  return {
    nodes: allNodes,
    edges: allEdges,
    positions: allPositions,
  };
};

export type ServerNodePosition = {
  artifactIdentifier: string;
  x: number;
  y: number;
};

/**
 * Converts the graph positions to a format that can be stored in the database.
 * GraphPositions uses tapeBuilderNodeIds (uuid), but the database needs stable artifact identifiers.
 * For UI Nodes:
 *  For tape nodes we use artifactSlugs as identifier ,
 *  For operation node we use: ${sourceSlugs}=>${operation}=>${targetSlugs}
 * For Db Node:
 *  All Db Nodes have artifactSlugs, so we use artifactSlugs as identifier.
 */
export const toServerPositions = ({
  positions,
  nodes,
  edges,
}: {
  positions: GraphPositions;
  nodes: TapeBuilderNodeAbstract[];
  edges: TapeBuilderEdge[];
}): ServerNodePosition[] => {
  const { normalized } = normalizeGraph({
    nodes,
    edges,
    positions,
  });

  const dbPositions: ServerNodePosition[] = [];
  Object.entries(normalized.positions).forEach(([id, { x, y }]) => {
    dbPositions.push({
      artifactIdentifier: id,
      x,
      y,
    });
  });
  return dbPositions;
};

export const fromServerPositions = ({
  dbPositions,
  nodes,
  edges,
}: {
  dbPositions: ServerNodePosition[];
  nodes: TapeBuilderNodeAbstract[];
  edges: TapeBuilderEdge[];
}): GraphPositions => {
  const { normalized, nodeIdToSlug } = normalizeGraph({
    nodes,
    edges,
    positions: {},
  });

  // overwrite normalized positions with dbPositions.
  normalized.positions = dbPositions.reduce<GraphPositions>(
    (acc, { artifactIdentifier, x, y }) => {
      // eslint-disable-next-line no-param-reassign
      acc[artifactIdentifier] = { x, y };
      return acc;
    },
    {},
  );

  const { positions } = deNormalizeGraph({
    normalized,
    nodeIdToSlug,
  });

  return positions;
};
